"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { object } from "yup";

import useInputValidation from "@/hooks/useInputValidation";
import { leadService } from "@/services/lead.service";
import { useModalStore } from "@/store/modal.store";
import { IAuthForm } from "@/types/auth.types";
import Button from "@/ui/Common/Button";
import FormField from "@/ui/Common/Forms/FormField";
import { HeadTitle } from "@/ui/Common/HeadTitle";

import styles from "./style.module.scss";

interface FormValues {
  email?: string;
}

export const SubscribeModal = () => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const { emailSchema } = useInputValidation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, isDirty, errors },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: { email: "" },
    resolver: yupResolver(
      object({
        email: emailSchema,
      }).required(),
    ),
  });

  const { mutate } = useMutation({
    mutationKey: ["recoveryPasswordFromPhone"],
    mutationFn: (data: Pick<IAuthForm, "email">) => leadService.subscribe(data),
    onError(error) {
      if (error?.status === 400) {
        return toast.error(t("Invalid email format"));
      }
      toast.error(t("Something went wrong, please try again later!"));
    },
    onSuccess() {
      toast.success(
        t("Subscription completed successfully! Confirmation sent by email!"),
      );
      reset();
      closeModal();
    },
  });

  const onSubmit = handleSubmit(async ({ email }) => {
    if (!email) {
      return;
    }
    mutate({ email });
    return;
  });

  return (
    <div className={styles.wrapper}>
      <HeadTitle title={t("Subscribe")} />
      <p className={styles.description}>
        {t("Leave your email to receive notifications about new objects")}
      </p>
      <form
        className={styles.btnContainer}
        onSubmit={onSubmit}
      >
        <FormField
          type="email"
          placeholder={t("email")}
          error={errors.email?.message}
          {...register("email")}
        />
        <Button
          type="gray"
          onClick={() => closeModal()}
        >
          {t("Close")}
        </Button>
        <Button disabled={!isDirty || !isValid}>{t("Save")}</Button>
      </form>
    </div>
  );
};
